import { io } from "socket.io-client";
import { TOKEN } from "../common/constants";

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.currentRoomId = null;
  }

  connect() {
    const token = localStorage.getItem(TOKEN);

    if (!token) {
      console.error("No token found. Cannot connect to socket.");
      return Promise.reject(new Error("No authentication token"));
    }

    // Don't reconnect if already connected
    if (this.socket && this.connected) {
      console.log("Socket already connected");
      return Promise.resolve(this.socket);
    }

    return new Promise((resolve, reject) => {
      // Connect to socket server
      this.socket = io(process.env.REACT_APP_SOCKET_URL, {
        auth: {
          token: token,
        },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        timeout: 10000,
      });

      // Connection events
      this.socket.on("connect", () => {
        console.log("✅ Socket connected:", this.socket.id);
        this.connected = true;
        resolve(this.socket);
      });

      this.socket.on("disconnect", (reason) => {
        console.log("❌ Socket disconnected:", reason);
        this.connected = false;
      });

      this.socket.on("connect_error", (error) => {
        console.error("⚠️ Socket connection error:", error);
        this.connected = false;
        reject(error);
      });

      this.socket.on("error", (error) => {
        console.error("⚠️ Socket error:", error);
      });

      // Handle reconnection
      this.socket.on("reconnect", (attemptNumber) => {
        console.log("🔄 Socket reconnected after", attemptNumber, "attempts");
        this.connected = true;

        // Rejoin the room if we were in one
        if (this.currentRoomId) {
          this.createChatRoom(this.currentRoomId);
        }
      });

      this.socket.on("reconnect_attempt", (attemptNumber) => {
        console.log("🔄 Reconnection attempt:", attemptNumber);
      });

      this.socket.on("reconnect_failed", () => {
        console.error("❌ Failed to reconnect to socket server");
      });
    });
  }

  disconnect() {
    if (this.socket) {
      // Leave current room before disconnecting
      if (this.currentRoomId) {
        this.leaveChatRoom({
          roomType: 1,
          roomId: this.currentRoomId,
        });
      }

      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.currentRoomId = null;
      console.log("👋 Socket disconnected manually");
    }
  }

  /**
   * Create/Join a chat room
   * @param {Object} params - Room parameters
   * @param {number} params.roomType - 1 = Patient-Doctor, 2 = Patient-Support
   * @param {number} params.userId - Current user ID
   * @param {number} params.patientId - Patient ID
   */
  createChatRoom({ roomType, userId, patientId }) {
    if (!this.socket || !this.connected) {
      console.error("Socket not connected. Cannot create room.");
      return;
    }

    const roomData = {
      roomType,
      userId,
      patientId,
    };

    this.socket.emit("createChatRoom", roomData);
    this.currentRoomId = patientId;
    console.log("📥 Joined room:", roomData);
  }

  /**
   * Leave a chat room
   */
  leaveChatRoom({ roomType = 1, roomId }) {
    if (!roomId) {
      console.error("roomId is required for leaveChatRoom");
      return;
    }

    if (!this.socket || !this.connected) {
      console.warn("Socket not connected. Cannot leave room.");
      return;
    }

    const roomData = {
      roomType,
      roomId,
    };

    this.socket.emit("leaveChatRoom", roomData);
    console.log("📤 Left room:", roomData);

    if (this.currentRoomId === roomId) {
      this.currentRoomId = null;
    }
  }

  /**
   * Send a message
   * @param {Object} params - Message parameters
   * @param {number} params.userId - Sender user ID
   * @param {number} params.roomId - Room ID
   * @param {number} params.roomType - 1 = Patient-Doctor, 2 = Patient-Support
   * @param {number} params.patientId - Patient ID
   * @param {number} params.messageType - 1 = text, 2 = image, 3 = file
   * @param {string} params.content - Message content
   */
  sendMessage({
    userId,
    roomId,
    roomType,
    patientId,
    messageType,
    content,
    fileUrl,
    file,
  }) {
    console.log("file, fileUrl", file, fileUrl);
    if (!this.socket || !this.connected)
      return console.error("Socket not connected");
    if (!roomId) return console.error("roomId is required");

    // Text messages require content, Image/File requires fileUrl
    if (messageType === 1 && !content)
      return console.error("Text message content missing");
    if (messageType === 2 && !fileUrl)
      return console.error("fileUrl is required for image/file message");

    const messageData = {
      userId,
      roomId,
      roomType,
      patientId,
      messageType,
      content: content || "",
      fileUrl: fileUrl || file || null,
      file: file || fileUrl,
    };

    this.socket.emit("sendMessage", messageData);
    console.log("💬 Message sent:", messageData);
  }

  onSidebarUpdate(cb) {
    this.socket?.off("sidebarUpdate");
    this.socket?.on("sidebarUpdate", (payload) => {
      console.log("🟣 sidebarUpdate:", payload);
      cb(payload);
    });
  }

  /**
   * Listen for new messages
   * @param {Function} callback - Callback function to handle new message
   */
  onNewMessage(callback) {
    if (this.socket) {
      this.socket.off("newMessage"); // Remove previous listener
      this.socket.on("newMessage", (data) => {
        console.log("📨 New message received:", data);
        callback(data);
      });
    }
  }

  /**
   * Listen for room joined event
   * @param {Function} callback - Callback function
   */
  onJoinedRoom(callback) {
    if (this.socket) {
      this.socket.off("joinedRoom");
      this.socket.on("joinedRoom", (data) => {
        console.log("✅ Joined room:", data);
        callback(data);
      });
    }
  }

  /**
   * Listen for message delivery confirmation
   * @param {Function} callback - Callback function
   */
  onMessageSent(callback) {
    if (this.socket) {
      this.socket.off("messageSent");
      this.socket.on("messageSent", (data) => {
        console.log("✅ Message sent confirmation:", data);
        callback(data);
      });
    }
  }

  /**
   * Listen for typing indicator
   * @param {Function} callback - Callback function
   */
  onTyping(callback) {
    if (this.socket) {
      this.socket.off("userTyping");
      this.socket.on("userTyping", (data) => {
        console.log("⌨️ User typing:", data);
        callback(data);
      });
    }
  }

  /**
   * Emit typing event
   * @param {number} roomId - Room ID
   * @param {boolean} isTyping - Is user typing
   */
  emitTyping(roomId, isTyping = true) {
    if (this.socket && this.connected && roomId) {
      this.socket.emit("typing", { roomId, isTyping });
    }
  }

  /**
   * Listen for user online/offline status
   * @param {Function} callback - Callback function
   */
  onUserStatus(callback) {
    if (this.socket) {
      this.socket.off("userStatus");
      this.socket.on("userStatus", (data) => {
        console.log("👤 User status:", data);
        callback(data);
      });
    }
  }

  /**
   * Listen for error events
   * @param {Function} callback - Callback function
   */
  onError(callback) {
    if (this.socket) {
      this.socket.off("error");
      this.socket.on("error", (error) => {
        console.error("⚠️ Socket error event:", error);
        callback(error);
      });
    }
  }

  /**
   * Remove specific listener
   * @param {string} event - Event name
   */
  off(event, cb) {
    this.socket?.off(event, cb);
  }

  /**
   * Remove all listeners
   */
  removeAllListeners() {
    if (this.socket) {
      this.socket.removeAllListeners();
    }
  }

  /**
   * Get socket instance
   */
  getSocket() {
    return this.socket;
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.connected && this.socket && this.socket.connected;
  }

  /**
   * Get current room ID
   */
  getCurrentRoom() {
    return this.currentRoomId;
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
